import { TrendingUp, DollarSign, Calendar, TrendingDown } from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { formatUsd } from '@/lib/utils';

export default function CostProjections() {
  const { rows } = useAppStore();

  if (rows.length === 0) {
    return (
      <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
        <h2 className="text-lg font-semibold mb-4 text-white">
          Cost Projections
        </h2>
        <div className="text-slate-400 text-center py-8">
          Configure your transfer settings to see projected costs over time.
        </div>
      </div>
    );
  }

  // Calculate cost projections
  const dailyCost = rows[0]?.dailyCost || 0;
  const weeklyCost = dailyCost * 7;
  const monthlyCost = dailyCost * 30;
  const yearlyCost = dailyCost * 365;
  const totalCost = rows.reduce((sum, row) => sum + row.dailyCost, 0);

  const projectionCards = [
    {
      title: 'Daily Cost',
      value: formatUsd(dailyCost),
      subtitle: 'Per day without staking',
      explanation: "What you pay daily if you don't stake TRX",
      icon: Calendar,
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20',
    },
    {
      title: 'Weekly Cost',
      value: formatUsd(weeklyCost),
      subtitle: 'Per week without staking',
      explanation: 'Weekly cost of daily fees',
      icon: Calendar,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/20',
    },
    {
      title: 'Monthly Cost',
      value: formatUsd(monthlyCost),
      subtitle: 'Per month without staking',
      explanation: 'Monthly cost of daily fees',
      icon: TrendingUp,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/20',
    },
    {
      title: 'Yearly Cost',
      value: formatUsd(yearlyCost),
      subtitle: 'Per year without staking',
      explanation: 'Annual cost of daily fees',
      icon: DollarSign,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20',
    },
  ];

  return (
    <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Cost Projections</h2>
          <p className="text-slate-400">
            See how much you'll spend over time if you don't stake TRX
          </p>
        </div>
        <div className="p-2 bg-red-500/10 rounded-lg border border-red-500/20">
          <TrendingDown className="h-5 w-5 text-red-400" />
        </div>
      </div>

      {/* Projection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {projectionCards.map((card, index) => (
          <div
            key={index}
            className={`p-4 rounded-xl border ${card.borderColor} ${card.bgColor} backdrop-blur-sm`}
          >
            <div className={`p-2 rounded-lg ${card.bgColor} mb-3 inline-block`}>
              <card.icon className={`h-5 w-5 ${card.color}`} />
            </div>
            <h3 className="font-semibold text-sm text-slate-300 mb-2">
              {card.title}
            </h3>
            <p className="text-2xl font-bold text-white mb-2">{card.value}</p>
            <p className="text-sm text-slate-400 mb-2">{card.subtitle}</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              {card.explanation}
            </p>
          </div>
        ))}
      </div>

      {/* Total Projection Cost */}
      <div className="p-6 bg-red-500/10 rounded-xl border border-red-500/20 mb-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-red-400 mb-2">
            Total Projection Cost
          </h3>
          <div className="text-4xl font-bold text-white mb-2">
            {formatUsd(totalCost)}
          </div>
          <p className="text-red-200">
            Total cost over {rows.length} days without staking TRX
          </p>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Cost Analysis */}
        <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
          <h4 className="font-medium mb-4 text-white">Cost Analysis</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Daily Average:</span>
              <span className="font-semibold text-white">
                {formatUsd(dailyCost)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Weekly Average:</span>
              <span className="font-semibold text-white">
                {formatUsd(weeklyCost)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Monthly Average:</span>
              <span className="font-semibold text-white">
                {formatUsd(monthlyCost)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Yearly Average:</span>
              <span className="font-semibold text-white">
                {formatUsd(yearlyCost)}
              </span>
            </div>
          </div>
        </div>

        {/* Comparison with Staking */}
        <div className="p-6 bg-green-500/10 rounded-xl border border-green-500/20">
          <h4 className="font-medium mb-4 text-green-400">
            Staking Alternative
          </h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-green-200">Daily Cost:</span>
              <span className="font-semibold text-green-400">$0.00</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-green-200">Weekly Cost:</span>
              <span className="font-semibold text-green-400">$0.00</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-green-200">Monthly Cost:</span>
              <span className="font-semibold text-green-400">$0.00</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-green-200">Yearly Cost:</span>
              <span className="font-semibold text-green-400">$0.00</span>
            </div>
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <h4 className="font-medium mb-4 text-white">Key Insights</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-300">
          <div>
            <h5 className="font-medium text-red-400 mb-2">Without Staking</h5>
            <ul className="space-y-1 text-slate-400">
              <li>• Daily fees add up quickly</li>
              <li>• Monthly costs can be significant</li>
              <li>• No upfront investment required</li>
              <li>• Costs increase with transfer volume</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-green-400 mb-2">With Staking</h5>
            <ul className="space-y-1 text-slate-400">
              <li>• One-time TRX investment</li>
              <li>• Daily renewable resource quota</li>
              <li>• Zero ongoing transaction fees</li>
              <li>• Investment pays for itself over time</li>
            </ul>
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
          <p className="text-sm text-blue-200">
            <strong>💡 Smart Strategy:</strong> By staking the optimal amount of
            TRX, you eliminate all daily costs and get a renewable resource
            quota that covers your needs. It's like buying a monthly pass
            instead of paying per ride!
          </p>
        </div>
      </div>
    </div>
  );
}
